/**
 * Mirror of `socaity_schemas.platform.pricing`.
 * Includes prediction shapes (`PriceEstimate`, `SimilarService`).
 */

const num = (v: any): number | null => (v != null ? Number(v) : null);

export interface PriceEstimate {
  estimated_price_eur: number | null;
  estimated_inference_time_s: number | null;
  /** True when the price is an exact unit-based calculation, not an ML prediction. */
  is_deterministic: boolean;
}

export class SimilarService {
  service_id: string | null;
  display_name: string | null;
  similarity: number;

  constructor(data: Record<string, any> = {}) {
    this.service_id = data.service_id ?? null;
    this.display_name = data.display_name ?? null;
    this.similarity = data.similarity != null ? Number(data.similarity) : 0;
  }
}

/** Per-endpoint user pricing: billed by runtime seconds or input/output units. */
export class EndpointPricing {
  service_id: string | null;
  deployment_id: string | null;
  endpoint_id: string | null;
  kind: 'runtime' | 'unit' | string;
  currency: string;
  cost_per_second: number | null;
  input_unit: string | null;
  cost_per_input_unit: number | null;
  output_unit: string | null;
  cost_per_output_unit: number | null;

  constructor(data: Record<string, any> = {}) {
    this.service_id = data.service_id ?? null;
    this.deployment_id = data.deployment_id ?? null;
    this.endpoint_id = data.endpoint_id ?? null;
    this.kind = data.kind || 'runtime';
    this.currency = data.currency || 'EUR';
    this.cost_per_second = num(data.cost_per_second);
    this.input_unit = data.input_unit ?? null;
    this.cost_per_input_unit = num(data.cost_per_input_unit);
    this.output_unit = data.output_unit ?? null;
    this.cost_per_output_unit = num(data.cost_per_output_unit);
  }

  get isRuntime(): boolean {
    return this.kind === 'runtime';
  }
}

/** Aggregated historical statistics for a single service endpoint. */
export class EndpointStats {
  deployment_id: string | null;
  endpoint: string | null;
  n_jobs: number;
  mean_price: number | null;
  mean_execution_time_s: number | null;
  mean_provider_execution_time_s: number | null;
  mean_provider_delay_time_s: number | null;
  mean_delay_s: number | null;
  currency: string;
  price_index: number | null;
  speed_index: number | null;
  imputed: boolean;

  constructor(data: Record<string, any> = {}) {
    this.deployment_id = data.deployment_id ?? null;
    this.endpoint = data.endpoint ?? null;
    this.n_jobs = data.n_jobs ?? 0;
    this.mean_price = num(data.mean_price);
    this.mean_execution_time_s = num(data.mean_execution_time_s);
    this.mean_provider_execution_time_s = num(data.mean_provider_execution_time_s);
    this.mean_provider_delay_time_s = num(data.mean_provider_delay_time_s);
    this.mean_delay_s = num(data.mean_delay_s);
    this.currency = data.currency || 'EUR';
    this.price_index = num(data.price_index);
    this.speed_index = num(data.speed_index);
    this.imputed = data.imputed === true;
  }
}

const CURRENCY_SYMBOLS: Record<string, string> = { USD: '$', EUR: '€', GBP: '£' };

export function formatCurrency(amount: any, currency = 'EUR', decimals = 6): string {
  const value = Number(amount);
  if (!Number.isFinite(value)) return 'n/a';
  const code = String(currency || 'EUR').toUpperCase();
  const symbol = CURRENCY_SYMBOLS[code] || `${code} `;
  return `${symbol}${value.toFixed(decimals)}`;
}

/** Compute pricing tier a deployment runs on (user-facing subset). */
export class HostingPricingRule {
  id: number | string | null;
  display_name: string;
  gpu_vram_gb: number;
  physical_gpus: string | null;
  cost_per_second: number;
  currency: string;
  minimum_charge_amount: number;
  minimum_charge_currency: string;
  is_active: boolean;
  provider: string | null;

  constructor(data: Record<string, any> = {}) {
    this.id = data.id ?? null;
    this.display_name = data.display_name ?? 'Unknown';
    this.gpu_vram_gb = Number(data.gpu_vram_gb) || 0;
    this.physical_gpus = data.physical_gpus ?? null;
    this.cost_per_second = num(data.cost_per_second) || 0;
    this.currency = data.currency || 'EUR';
    this.minimum_charge_amount = num(data.minimum_charge_amount) || 0;
    this.minimum_charge_currency = data.minimum_charge_currency || 'EUR';
    this.is_active = data.is_active === true;
    this.provider = data.provider ?? null;
  }

  get providerKey(): string {
    return String(this.provider || '').toLowerCase().trim();
  }

  /** Number of physical GPUs inferred from the display name prefix (e.g. "2x Nvidia ..." → 2). */
  get gpuCount(): number {
    const match = /^(\d+)\s*x/i.exec(this.display_name || '');
    return match ? parseInt(match[1], 10) : 1;
  }

  get userPricePerSecond(): number {
    return this.cost_per_second;
  }

  get userPriceLabel(): string {
    return formatCurrency(this.cost_per_second, this.currency);
  }
}
